import { createContext } from "react";

import type { FieldErrors } from "./client.js";

/**
 * ファイルのダウンロード（CSV）。<b>画面から fetch で受け取り、Blob にして保存させる</b>（docs/02 §8・§9）。
 *
 * ブラウザでその URL へ移る形（素の <c>&lt;a href&gt;</c>）だと、API が遅くて SWA が打ち切ったとき・断ったときに
 * 応答の本文（SWA の素の文字など）へ画面ごと移ってしまう。fetch で受ければ、失敗しても画面は残り、断りを画面の欄に出せる。
 * <b>読むだけの口なので CSRF トークンは添えない</b>（<c>getJson</c> と同じ。Cookie は同じオリジンなので付く）。
 */

/** 受け取った結果。 */
export type DownloadOutcome =
    /** 受け取れた。名前は応答の <c>Content-Disposition</c> から（無ければ undefined。呼ぶ側の規則で付ける）。 */
    | { readonly kind: "file"; readonly blob: Blob; readonly fileName: string | undefined }
    /** ログインしていない・切れた（401。またはログインの画面へ移された）。 */
    | { readonly kind: "unauthorized" }
    /**
     * 受け取れなかった。status は応答の状態コード（通信できなければ無い）。
     * errors は 400 の欄ごとの断り。<b>notCsv は 200 でも中身が CSV でなかった</b>とき（SWA の素の文字など）。
     */
    | {
        readonly kind: "failed";
        readonly status?: number;
        readonly errors?: FieldErrors | undefined;
        readonly notCsv?: boolean;
    };

/** 断りの本文（ValidationProblemDetails）。 */
interface Problem {
    readonly errors?: Record<string, string[]>;
}

const problemErrors = async (response: Response): Promise<FieldErrors | undefined> => {
    try {
        return (await response.json() as Problem).errors;
    } catch {
        return undefined;
    }
};

/** 引用符を外す（<c>filename="a b.csv"</c>）。 */
const unquote = (value: string): string =>
    value.startsWith("\"") && value.endsWith("\"") && value.length >= 2
        ? value.slice(1, -1).replaceAll(/\\(.)/gu, "$1")
        : value;

/**
 * <c>Content-Disposition</c> から保存する名前を読む。<b><c>filename*</c>（UTF-8 の符号化）を先に見る</b>（RFC 6266）。
 * 読めなければ undefined。<b>パスの区切りは落とす</b>（名前だけを使う）。
 */
export const fileNameFrom = (header: string | null): string | undefined => {
    if (header === null) {
        return undefined;
    }
    const parts = header.split(";").map((o) => o.trim());
    const valueOf = (key: string): string | undefined => {
        const part = parts.find((o) => o.toLowerCase().startsWith(`${key}=`));
        return part?.slice(key.length + 1).trim();
    };

    let name: string | undefined;
    const extended = valueOf("filename*");
    const encoded = extended === undefined ? undefined : /^utf-8'[^']*'(?<value>.+)$/iu.exec(unquote(extended))?.groups?.["value"];
    if (encoded !== undefined) {
        try {
            name = decodeURIComponent(encoded);
        } catch {
            name = undefined;
        }
    }
    if (name === undefined) {
        const plain = valueOf("filename");
        name = plain === undefined ? undefined : unquote(plain);
    }
    const base = name?.split(/[/\\]/u).pop()?.trim();
    return base === undefined || base === "" ? undefined : base;
};

/** 応答が CSV か（<c>text/csv</c>）。SWA の打ち切りは素の文字（<c>text/plain</c> など）で返る。 */
const isCsv = (response: Response): boolean =>
    (response.headers.get("content-type") ?? "").toLowerCase().startsWith("text/csv");

/**
 * ログインの画面へ移されたか。<b>CSV の口は、URL を直接開いたとき（<c>Accept</c> に JSON が無い）の未ログインを
 * 401 ではなくログインの画面へ移す</b>（Implem.Portal.Backend の <c>docs/features/29</c>）。ここは <c>Accept</c> に
 * JSON を添えるので 401 が返るが、移されたときも fetch はその移り先をたどって 200 の HTML を受け取るので、移った先のパスで見分ける。
 */
const redirectedToLogin = (response: Response): boolean => {
    if (!response.redirected) {
        return false;
    }
    try {
        return new URL(response.url).pathname.toLowerCase() === "/account/login";
    } catch {
        return false;
    }
};

/**
 * CSV を受け取る。<b>本文を読み切るまで</b>待つ（途中で切れたら通信の失敗として返す）。
 * 通信できなかったときも例外にせず <c>failed</c> で返す。
 */
export const fetchCsv = async (url: string): Promise<DownloadOutcome> => {
    try {
        const response = await fetch(url, { headers: { Accept: "text/csv, application/json" } });
        if (response.status === 401 || redirectedToLogin(response)) {
            return { kind: "unauthorized" };
        }
        if (!response.ok) {
            return response.status === 400
                ? { kind: "failed", status: response.status, errors: await problemErrors(response) }
                : { kind: "failed", status: response.status };
        }
        if (!isCsv(response)) {
            return { kind: "failed", status: response.status, notCsv: true };
        }
        const blob = await response.blob();
        return { kind: "file", blob, fileName: fileNameFrom(response.headers.get("content-disposition")) };
    } catch {
        return { kind: "failed" };
    }
};

/**
 * 受け取ったものを保存させる。<b>Blob の URL を <c>download</c> 付きのリンクで開く</b>（画面は移らない）。
 * URL はすぐには捨てない——ブラウザによっては保存を始める前に読めなくなる。
 */
export const saveFile = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.hidden = true;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => { URL.revokeObjectURL(url); }, 60_000);
};

/** 保存のしかた。<b>テストで差し替える</b>（jsdom は保存できない）。 */
export const SaveFileContext = createContext<(blob: Blob, fileName: string) => void>(saveFile);
